/**
 * NewDmModal — picker for starting a new DM. Lists `/api/chat/targets`
 * (server determines who you're allowed to message based on your role).
 * Extracted from MessagesPage for parity with MessageBubble.
 */
import React, { useEffect, useMemo, useState } from "react";

import LoadingSpinner from "../LoadingSpinner";
import Modal from "../Modal";
import { useAuth } from "../../contexts/AuthContext";
import {
  type ChatUser,
  fetchChatTargets,
  startChatDm,
} from "../../services/chatApi";

export const NewDmModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onPicked: (threadId: number) => void;
}> = ({ open, onClose, onPicked }) => {
  const { token } = useAuth();
  const [targets, setTargets] = useState<ChatUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open || !token) return;
    setTargets(null);
    setQ("");
    fetchChatTargets(token)
      .then((d) => setTargets(d.targets))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [open, token]);

  // Case-insensitive substring match against name / email / company /
  // role so a "Jason" or "@nabors.com" or "vendor" search all work.
  const filtered = useMemo(() => {
    if (!targets) return null;
    const needle = q.trim().toLowerCase();
    if (!needle) return targets;
    return targets.filter((u) => {
      const hay = [u.name, u.email, u.company_name, u.role]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [targets, q]);

  const pick = async (uid: number): Promise<void> => {
    if (!token) return;
    try {
      const r = await startChatDm(uid, token);
      onPicked(r.thread_id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Start a new DM" widthClass="max-w-md">
      {error && (
        <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded mb-3">{error}</div>
      )}
      {targets === null ? (
        <LoadingSpinner label="loading users" />
      ) : targets.length === 0 ? (
        <div className="text-sm text-secondary-500">No users available to DM.</div>
      ) : (
        <>
          <input
            type="search"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, or company…"
            className="w-full border border-secondary-300 rounded px-2 py-1.5 text-sm mb-2"
          />
          {filtered && filtered.length === 0 ? (
            <div className="text-sm text-secondary-500 py-2">
              No matches for "{q.trim()}".
            </div>
          ) : (
            <ul className="divide-y divide-secondary-100 max-h-96 overflow-y-auto">
              {filtered!.map((u) => (
                <li key={u.user_id}>
                  <button
                    type="button"
                    onClick={() => void pick(u.user_id)}
                    className="w-full text-left px-2 py-2 hover:bg-secondary-50 rounded"
                  >
                    <div className="font-semibold text-sm">
                      {u.name || u.email || `User ${u.user_id}`}
                    </div>
                    <div className="text-xs text-secondary-500">
                      {u.email}
                      {u.company_name ? ` · ${u.company_name}` : ""}
                      {u.role ? ` · ${u.role}` : ""}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Modal>
  );
};

export default NewDmModal;
